<!--
  Login.svelte — Primary sign-in page.

  Defaults to email-based passwordless login (OTP). Users enter their
  email address, receive a 6-digit code, and are signed in. If no
  account exists, one is automatically created on the server side.

  Also supports:
    - Google Sign-In (via GSI button, if configured)
    - Sign in with Apple (if configured)
    - Sign in with Microsoft (if configured)
    - Email + password (toggle link for existing password users)

  On successful login the JWT is stored and the user is redirected
  to the dashboard.  If the server responds with `requiresTwoFactor:
  true`, the user is redirected to `/two-factor`.
-->
<script lang="ts">
	import { preventDefault } from 'svelte/legacy';

	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import { Button, Hr, Checkbox } from 'flowbite-svelte';
	import { t, t2 } from '../../i18n';
	import { push, querystring } from '../../abstract-spa-router';
	import AuthLayout from './AuthLayout.svelte';
	import { Spinner } from 'flowbite-svelte';
	import {
		FormField,
		PasswordField,
		AlertMessage,
		GoogleSignInButton,
		AppleSignInButton,
		MicrosoftSignInButton,
		OTPInput,
		RoutedLink
	} from '../../components';
	import AppBrandingCard from '../../components/AppBrandingCard.svelte';
	import { setToken, refreshTokenPeriodically, preferredAuthMethod, type AuthMethod } from '../../Auth';
	import {
		login,
		loginGoogle,
		loginApple,
		loginMicrosoft,
		requestEmailOTP,
		verifyEmailOTP,
		isValidEmail,
		passkeyAuthOptions,
		passkeyAuthVerify,
		getApplicationPublicInfo,
		savePreferredAuthMethod
	} from '../../AppNetwork';
	import { configurationStore } from '../../AppConfiguration';
	import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
	import type { ApplicationPublicInfo } from '../../AppTypes';

	// ── App consent context ─────────────────────────────────

	/**
	 * When an external application's authorization flow redirects here
	 * (via `/login?app=UUID&returnURL=...` or with OAuth params), we
	 * read the parameters from the URL query string and fetch the app's
	 * public info for branded display. After login, the user is sent
	 * to `/authorize?...` reconstructed from those same params. All
	 * state lives in the URL — no sessionStorage.
	 *
	 * Two URL shapes are supported:
	 *  1. Top-level params (current Authorize.svelte handoff):
	 *     `/login?app=UUID&returnURL=...` (or with oauth=true + client_id…).
	 *     The /authorize URL is rebuilt from every param on this page.
	 *  2. Legacy `redirect=` (used by BackCompatRouter invitation
	 *     emails): `/login?app=UUID&email=<invitee>&redirect=/authorize?...`.
	 *     We push to that explicit path verbatim after login. The
	 *     `email` param pre-fills the form and auto-requests a sign-in
	 *     code so invitees land on the OTP screen with no clicks.
	 */

	/** URL to redirect to after login (the original /authorize?... URL). */
	let redirectAfterLogin = '';

	onMount(async () => {
		const params = new URLSearchParams($querystring || '');
		const appParam = params.get('app');
		const explicitRedirect = params.get('redirect');

		/* Resolve the post-login destination:
		   - If `redirect=` was passed (legacy / invitation flow), honour it.
		   - Otherwise rebuild the /authorize URL from the top-level params
		     so the URL bar stays single-encoded and human-readable. We
		     drop `email` (form-prefill only — not an authorize param). */
		if (explicitRedirect) {
			redirectAfterLogin = explicitRedirect;
		} else if (appParam) {
			const authorizeParams = new URLSearchParams(params);
			authorizeParams.delete('email');
			redirectAfterLogin = `/authorize?${authorizeParams.toString()}`;
		} else {
			redirectAfterLogin = '';
		}

		/** Fetch branded app info when an app context is provided. */
		if (appParam) {
			/* Fetch public app info for branded display. */
			try {
				appInfo = await getApplicationPublicInfo(appParam);
			} catch {
				/* App doesn't exist or is inactive — clear redirect so the user
				   isn't sent to /authorize for a non-existent app after login. */
				redirectAfterLogin = '';
				errorMessage = t2('The application you were trying to sign in to was not found or is no longer active.');
			}
		}

		const prefilledEmail = params.get('email');
		/**
		 * Invitation flow: if the URL carries a pre-selected email, fill it in
		 * and kick off the OTP request automatically so the invitee lands on
		 * the 6-digit code input without any further interaction. Skipped if
		 * app info failed to load so the error message stays visible.
		 */
		if (prefilledEmail && !errorMessage) {
			email = prefilledEmail;
			await handleContinue();
		}
	});

	/** Returns true if an external app authorization flow is active. */
	function hasAppContext(): boolean {
		return !!redirectAfterLogin;
	}

	/**
	 * Build the two-factor URL, forwarding the post-login destination so
	 * the 2FA page can show branding and redirect correctly.
	 *
	 * Carries the current querystring through unchanged (top-level params:
	 * app, returnURL, oauth, …) so TwoFactor.svelte can rebuild the
	 * /authorize URL the same way Login does. For the legacy
	 * `redirect=`-style invitation flow, the existing `redirect=` param
	 * passes through automatically as part of `$querystring`. The OTP
	 * `email` pre-fill is dropped — it's a Login-only concern.
	 *
	 * Also forwards the method used and the "Remember my choice" state so
	 * TwoFactor.svelte can persist the preference after the 2FA step
	 * completes (the login page never reaches `completeLogin` in that case).
	 *
	 * @param twoFactorToken - The short-lived 2FA-pending token.
	 * @param method         - The method used to reach the 2FA challenge, or
	 *   `undefined` for social sign-in (not a rememberable choice).
	 */
	function buildTwoFactorUrl(twoFactorToken: string, method?: AuthMethod): string {
		const qs = new URLSearchParams($querystring || '');
		qs.delete('email');
		qs.set('twoFactorToken', twoFactorToken);
		/* Forward the remember state only for rememberable methods. */
		if (method) {
			qs.set('authMethod', method);
			qs.set('remember', rememberChoice ? '1' : '0');
		}
		return `/two-factor?${qs.toString()}`;
	}

	// ── Form state ────────────────────────────────────────────

	/** Email address entered by the user. */
	let email = $state('');
	/** Password entered by the user (password mode only). */
	let password = $state('');
	/** Whether a network request is in progress. */
	let loading = $state(false);
	/** Top-level error message shown above the form. */
	let errorMessage = $state('');

	/** Public info about the requesting application (for branded UI). */
	let appInfo: ApplicationPublicInfo | null = $state(null);

	/** Whether the Google Sign-In button should be visible (config loading or Google configured). */
	let showGoogleButton = $derived(!$configurationStore || !!$configurationStore.googleClientId);

	/** Whether the Apple sign-in button should be visible (config loading or Apple configured). */
	let showAppleButton = $derived(!$configurationStore || !!$configurationStore.appleClientId);

	/** Whether the Microsoft sign-in button should be visible (config loading or Microsoft configured). */
	let showMicrosoftButton = $derived(!$configurationStore || !!$configurationStore.microsoftClientId);

	/** Whether any social provider is offered, which decides who draws the "or" divider. */
	let showSocialButtons = $derived(showGoogleButton || showAppleButton || showMicrosoftButton);

	// ── Field-level errors ────────────────────────────────────

	/** Validation error for the email field. */
	let emailError = $state('');
	/** Validation error for the password field. */
	let passwordError = $state('');

	// ── View mode ─────────────────────────────────────────────

	/**
	 * Current view mode:
	 *   - 'email'      — default: just email input + Continue
	 *   - 'otp-verify' — 6-digit code input after OTP was sent
	 *   - 'password'   — email + password form for existing users
	 */
	let mode: 'email' | 'otp-verify' | 'password' = $state('email');

	/** Email address captured when OTP was requested (for verification). */
	let otpEmail = $state('');

	// ── Remembered method ─────────────────────────────────────

	/**
	 * Whether the user ticked "Remember my choice". When ticked, the method
	 * used to sign in is persisted on completion — both to localStorage and
	 * to the user's account (so the login page can pre-select it on the next
	 * visit, on any device). Initialised from the saved local preference so
	 * the box reflects a previously-remembered choice.
	 */
	let rememberChoice = $state(false);


	// ── Validation ────────────────────────────────────────────

	/** Clear all field-level errors. */
	function clearErrors(): void {
		emailError = '';
		passwordError = '';
		errorMessage = '';
	}

	/**
	 * Validate the email-only form.
	 * @returns `true` if the email is valid.
	 */
	function validateEmailForm(): boolean {
		clearErrors();
		/** Ensure the email field is not empty. */
		if (!email.trim()) {
			emailError = t2('Email is required.');
			return false;
		}
		/** Verify the email format is valid. */
		if (!isValidEmail(email.trim())) {
			emailError = t2('Please enter a valid email address.');
			return false;
		}
		return true;
	}

	/**
	 * Validate the email + password form.
	 * @returns `true` if inputs are valid.
	 */
	function validatePasswordForm(): boolean {
		clearErrors();
		let valid = true;

		/** Validate the email field. */
		if (!email.trim()) {
			emailError = t2('Email is required.');
			valid = false;
		} else if (!isValidEmail(email.trim())) {
			emailError = t2('Please enter a valid email address.');
			valid = false;
		}
		/** Validate the password field. */
		if (!password) {
			passwordError = t2('Password is required.');
			valid = false;
		}

		return valid;
	}

	// ── Auth handlers ─────────────────────────────────────────

	/**
	 * Complete the login by storing the token, starting the refresh
	 * timer, persisting the remembered method, and navigating to the
	 * authorize page (if an app context is present) or the dashboard.
	 *
	 * @param token  - The issued JWT.
	 * @param method - The method used to sign in (for "Remember my choice").
	 *   Omitted for Google sign-in, which is not a rememberable method.
	 */
	async function completeLogin(token: string, method?: AuthMethod): Promise<void> {
		setToken(token);
		refreshTokenPeriodically();

		/* Persist the remembered preference before navigating away — the
		   admin redirect is a full page load that would otherwise cancel
		   the in-flight account update. */
		await savePreferredAuthMethod(method, rememberChoice);

		/** Navigate to the app's authorize page or the admin dashboard. */
		if (hasAppContext()) {
			push(redirectAfterLogin);
		} else {
			/* Cross-app navigation to the admin app. */
			window.location.href = '/Manage/';
		}
	}

	/**
	 * Send a sign-in code to the entered email address, or switch to the
	 * user's preferred method if the server reports one.
	 *
	 * The server consults the account's saved preference: `password` /
	 * `passkey` switch the view (no code sent), `email` means an OTP was
	 * sent. Pass `force` to always send an OTP regardless of preference
	 * (used by "Sign in with email code instead" and "Resend code").
	 *
	 * @param force - Bypass the account preference and send an OTP.
	 */
	async function requestCode(force: boolean = false): Promise<void> {
		if (!validateEmailForm()) return;

		loading = true;
		/** Resolve the method (or send the OTP) and transition the view. */
		try {
			otpEmail = email.trim();
			const { method } = await requestEmailOTP(otpEmail, force);
			if (method === 'password') {
				mode = 'password';
			} else if (method === 'passkey') {
				await handlePasskeyLogin();
			} else {
				mode = 'otp-verify';
			}
		} catch (err: any) {
			errorMessage = err?.message || t2('Failed to send code. Please try again.');
		} finally {
			loading = false;
		}
	}

	/**
	 * Handle the email form's "Continue" — resolve the preferred method.
	 * A zero-arg wrapper so DOM event handlers don't pass the event object
	 * as `requestCode`'s `force` argument.
	 */
	function handleContinue(): Promise<void> {
		return requestCode(false);
	}

	/**
	 * Verify the 6-digit OTP code and complete login.
	 */
	async function handleVerifyOTP(code: string): Promise<void> {
		loading = true;
		errorMessage = '';
		/** Verify the OTP and handle two-factor or direct login. */
		try {
			const result = await verifyEmailOTP(otpEmail, code);

			if (result.requiresTwoFactor && result.twoFactorToken) {
				push(buildTwoFactorUrl(result.twoFactorToken, 'email'));
				return;
			}

			await completeLogin(result.token, 'email');
		} catch (err: any) {
			errorMessage = err?.message || t2('Invalid code. Please try again.');
		} finally {
			loading = false;
		}
	}

	/**
	 * Handle the email + password form submission.
	 * Redirects to 2FA page if the server requests it.
	 */
	async function handlePasswordLogin(): Promise<void> {
		if (!validatePasswordForm()) return;

		loading = true;
		/** Authenticate with email and password, handling 2FA if required. */
		try {
			const result = await login(email, password);

			if (result.requiresTwoFactor && result.twoFactorToken) {
				push(buildTwoFactorUrl(result.twoFactorToken, 'password'));
				return;
			}

			await completeLogin(result.token, 'password');
		} catch (err: any) {
			errorMessage = err?.message || t2('Login failed. Please try again.');
		} finally {
			loading = false;
		}
	}

	/**
	 * Handle the Google Sign-In credential callback.
	 */
	async function handleGoogleCredential(credential: string): Promise<void> {
		loading = true;
		errorMessage = '';
		/** Exchange the Google credential for a session token. */
		try {
			const result = await loginGoogle(credential);

			if (result.requiresTwoFactor && result.twoFactorToken) {
				push(buildTwoFactorUrl(result.twoFactorToken));
				return;
			}

			await completeLogin(result.token);
		} catch (err: any) {
			errorMessage = err?.message || t2('Google sign-in failed. Please try again.');
		} finally {
			loading = false;
		}
	}

	/**
	 * Handle the Sign in with Apple callback.
	 *
	 * @param code - Authorization code from Apple's popup.
	 * @param name - Name Apple disclosed, present only on a first authorisation.
	 */
	async function handleAppleCredential(
		code: string,
		name?: { firstName?: string; lastName?: string }
	): Promise<void> {
		loading = true;
		errorMessage = '';
		/** Exchange the Apple authorization code for a session token. */
		try {
			const result = await loginApple(code, name);

			if (result.requiresTwoFactor && result.twoFactorToken) {
				push(buildTwoFactorUrl(result.twoFactorToken));
				return;
			}

			await completeLogin(result.token);
		} catch (err: any) {
			errorMessage = err?.message || t2('Apple sign-in failed. Please try again.');
		} finally {
			loading = false;
		}
	}

	/**
	 * Handle the Sign in with Microsoft callback.
	 *
	 * @param code - Authorization code relayed from the popup callback page.
	 */
	async function handleMicrosoftCredential(code: string): Promise<void> {
		loading = true;
		errorMessage = '';
		/** Exchange the Microsoft authorization code for a session token. */
		try {
			const result = await loginMicrosoft(code);

			if (result.requiresTwoFactor && result.twoFactorToken) {
				push(buildTwoFactorUrl(result.twoFactorToken));
				return;
			}

			await completeLogin(result.token);
		} catch (err: any) {
			errorMessage = err?.message || t2('Microsoft sign-in failed. Please try again.');
		} finally {
			loading = false;
		}
	}

	/** Handle Enter key on the email form. */
	function handleEmailKeypress(event: KeyboardEvent): void {
		if (event.key === 'Enter') handleContinue();
	}

	/** Handle Enter key on the password form. */
	function handlePasswordKeypress(event: KeyboardEvent): void {
		if (event.key === 'Enter') handlePasswordLogin();
	}

	// ── Passkey state ─────────────────────────────────────────

	/** Whether the browser supports WebAuthn. */
	let passkeySupported = $state(false);
	/** Whether a passkey auth flow is in progress. */
	let passkeyLoading = $state(false);

	onMount(() => {
		passkeySupported = browserSupportsWebAuthn();

		/* Pre-tick "Remember my choice" when a local preference exists, so
		   the box reflects a previously-remembered choice and can be undone.
		   The view itself stays email-first — switching to the preferred
		   method happens server-side after the user clicks Continue. */
		if (get(preferredAuthMethod)) {
			rememberChoice = true;
		}
	});

	/**
	 * Start passkey authentication (discoverable credentials).
	 * No email is required — the authenticator selects the credential.
	 */
	async function handlePasskeyLogin(): Promise<void> {
		passkeyLoading = true;
		errorMessage = '';
		/** Run the full WebAuthn authentication ceremony. */
		try {
			/* Step 1: Get authentication options from server. */
			const { options, challenge } = await passkeyAuthOptions();

			/* Step 2: Prompt user via browser WebAuthn API. */
			const assertionResp = await startAuthentication({ optionsJSON: options as any });

			/* Step 3: Verify on server and get the JWT. */
			const result = await passkeyAuthVerify(challenge, assertionResp);

			if (result.requiresTwoFactor && result.twoFactorToken) {
				push(buildTwoFactorUrl(result.twoFactorToken, 'passkey'));
				return;
			}

			await completeLogin(result.token, 'passkey');
		} catch (err: any) {
			/* User cancelled the WebAuthn dialog — not an error. */
			if (err?.name === 'NotAllowedError') {
				passkeyLoading = false;
				return;
			}
			errorMessage = err?.message || t2('Passkey sign-in failed. Please try again.');
		} finally {
			passkeyLoading = false;
		}
	}
</script>

<AuthLayout heading={appInfo ? '' : $t('Sign in or Register')} subheading={appInfo ? '' : $t('Enter your email to get started.')}>
	{#if appInfo}
		<AppBrandingCard
			applicationName={appInfo.applicationName}
			description={appInfo.description}
			logoImageName={appInfo.logoImageName}
			iconImageName={appInfo.iconImageName}
			icon={appInfo.icon}
		/>
	{/if}

	{#if errorMessage}
		<AlertMessage type="error" message={errorMessage} onclose={() => (errorMessage = '')} />
	{/if}

	<!-- ── Default: email input ──────────────────────────────── -->
	{#if mode === 'email'}
		<form onsubmit={preventDefault(handleContinue)}>
			<FormField
				id="login-email"
				label={$t("Email")}
				type="email"
				placeholder={$t("you@example.com")}
				bind:value={email}
				error={emailError}
				required
				autofocus
				onkeypress={handleEmailKeypress}
			/>

			<Button type="submit" class="w-full" disabled={loading}>
				{#if loading}<Spinner size="5" class="mr-2" />{/if}
				{$t("Continue")}
			</Button>
		</form>

		<!-- Social sign-in (shown while config loading or when configured) -->
		{#if showSocialButtons}
			<Hr class="my-5">
				<span class="px-2 text-sm text-gray-400 dark:text-gray-500">{$t("or")}</span>
			</Hr>

			{#if showGoogleButton}
				<GoogleSignInButton oncredential={handleGoogleCredential} text="signin_with" />
			{/if}

			{#if showAppleButton}
				<div class={showGoogleButton ? 'mt-3' : ''}>
					<AppleSignInButton oncredential={handleAppleCredential} text="signin_with" />
				</div>
			{/if}

			{#if showMicrosoftButton}
				<div class={showGoogleButton || showAppleButton ? 'mt-3' : ''}>
					<MicrosoftSignInButton oncredential={handleMicrosoftCredential} text="signin_with" />
				</div>
			{/if}
		{/if}

		<!-- Passkey sign-in (shown when browser supports WebAuthn) -->
		{#if passkeySupported}
			{#if !showSocialButtons}
				<Hr class="my-5">
					<span class="px-2 text-sm text-gray-400 dark:text-gray-500">{$t("or")}</span>
				</Hr>
			{/if}

			<Button
				color="alternative"
				class="mt-3 w-full"
				disabled={passkeyLoading || loading}
				onclick={handlePasskeyLogin}
			>
				{#if passkeyLoading}
					<Spinner size="5" class="mr-2" />
				{:else}
					<i class="fa-solid fa-fingerprint mr-2"></i>
				{/if}
				{$t("Sign in with a passkey")}
			</Button>
		{/if}

		<!-- Password toggle (tertiary) -->
		<Button
			color="alternative"
			class="mt-3 w-full"
			disabled={loading}
			onclick={() => {
				clearErrors();
				mode = 'password';
			}}
		>
			<i class="fa-solid fa-lock mr-2"></i>
			{$t("Sign in with password")}
		</Button>

		<!-- Remember the chosen sign-in method for next time -->
		<div class="mt-4 flex justify-center">
			<Checkbox bind:checked={rememberChoice}>
				{$t("Remember my choice")}
			</Checkbox>
		</div>

		<!-- ── OTP verification ──────────────────────────────────── -->
	{:else if mode === 'otp-verify'}
		<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
			{$t("We sent a 6-digit code to")} <strong class="text-gray-700 dark:text-gray-300">{otpEmail}</strong>. {$t("Enter it below.")}
		</p>

		<OTPInput oncomplete={handleVerifyOTP} error={errorMessage ? ' ' : ''} />

		<div class="mt-4 flex justify-center gap-4">
			<button
				type="button"
				class="text-sm text-primary-600 hover:underline dark:text-primary-400"
				disabled={loading}
				onclick={() => requestCode(true)}
			>
				<i class="fa-solid fa-rotate-right mr-1"></i> {$t("Resend code")}
			</button>
			<span class="text-gray-300 dark:text-gray-600">|</span>
			<button
				type="button"
				class="text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
				onclick={() => {
					clearErrors();
					mode = 'password';
				}}
			>
				<i class="fa-solid fa-lock mr-1"></i> {$t("Use password")}
			</button>
			{#if passkeySupported}
				<span class="text-gray-300 dark:text-gray-600">|</span>
				<button
					type="button"
					class="text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
					disabled={passkeyLoading}
					onclick={handlePasskeyLogin}
				>
					<i class="fa-solid fa-fingerprint mr-1"></i> {$t("Use passkey")}
				</button>
			{/if}
		</div>

		<div class="mt-5">
			<button
				type="button"
				class="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
				onclick={() => {
					clearErrors();
					mode = 'email';
				}}
			>
				<i class="fa-solid fa-arrow-left text-xs"></i>
				{$t("Back")}
			</button>
		</div>

		<!-- ── Password login (opt-in for existing users) ────────── -->
	{:else if mode === 'password'}
		<form onsubmit={preventDefault(handlePasswordLogin)}>
			<FormField
				id="login-email"
				label={$t("Email")}
				type="email"
				placeholder={$t("you@example.com")}
				bind:value={email}
				error={emailError}
				required
				onkeypress={handlePasswordKeypress}
			/>

			<PasswordField
				id="login-password"
				label={$t("Password")}
				placeholder={$t("••••••••")}
				bind:value={password}
				error={passwordError}
				required
				onkeypress={handlePasswordKeypress}
			/>

			<div class="mb-4 flex justify-end">
				<RoutedLink to="/forgot-password">
					<span class="text-sm text-primary-600 hover:underline dark:text-primary-400">{$t("Forgot password?")}</span>
				</RoutedLink>
			</div>

			<Button type="submit" class="w-full" disabled={loading}>
				{#if loading}<Spinner size="5" class="mr-2" />{/if}
				{$t("Sign in")}
			</Button>
		</form>

		<!-- Remember the chosen sign-in method for next time -->
		<div class="mt-4 flex justify-center">
			<Checkbox bind:checked={rememberChoice}>
				{$t("Remember my choice")}
			</Checkbox>
		</div>

		<div class="mt-4 text-center">
			<button
				type="button"
				class="text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
				disabled={loading}
				onclick={() => requestCode(true)}
			>
				<i class="fa-solid fa-envelope mr-1"></i> {$t("Sign in with email code instead")}
			</button>
			{#if passkeySupported}
				<span class="mx-1 text-gray-300 dark:text-gray-600">|</span>
				<button
					type="button"
					class="text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
					disabled={passkeyLoading}
					onclick={handlePasskeyLogin}
				>
					<i class="fa-solid fa-fingerprint mr-1"></i> {$t("Use a passkey")}
				</button>
			{/if}
		</div>
	{/if}
</AuthLayout>
